import {
  canonicalPracticeDeckCode,
  practiceDeckCodes,
} from './raidPracticeMatcher.js';

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function textValue(value) {
  return String(value ?? '').trim();
}

function memberKey(record = {}) {
  return textValue(record.memberOpenid ?? record.member_openid ?? record.openid);
}

function memberNickname(record = {}) {
  return textValue(record.memberNickname ?? record.member_nickname ?? record.nickname);
}

function normalizedMemberKey(value) {
  return textValue(value).toLocaleLowerCase('ko-KR');
}

function candidateKey(candidate) {
  return `${candidate.bossNo}:${candidate.deckSignature}`;
}

function canonicalRecommendationDeckCode(value, catalog) {
  return canonicalPracticeDeckCode(value, catalog);
}

export function normalizePracticeRecommendationCandidate(record = {}, index = 0, { catalog = null } = {}) {
  const deck = practiceDeckCodes(record.deck ?? record.deck_codes ?? record.deckCodes, catalog);
  const canonicalDeck = deck.map((code) => canonicalRecommendationDeckCode(code, catalog)).filter(Boolean);
  const uniqueDeck = new Set(canonicalDeck);
  const damageEok = numberValue(record.damageEok ?? record.damage_eok ?? record.damage);

  if (canonicalDeck.length !== 5 || uniqueDeck.size !== 5 || damageEok <= 0) return null;

  return {
    id: textValue(record.id) || `practice-${index + 1}`,
    memberOpenid: memberKey(record),
    memberNickname: memberNickname(record),
    bossNo: numberValue(record.bossNo ?? record.boss_no),
    slotNo: numberValue(record.slotNo ?? record.slot_no, index + 1),
    title: textValue(record.title) || `모의전 슬롯 ${numberValue(record.slotNo ?? record.slot_no, index + 1)}`,
    damageEok,
    deck,
    canonicalDeck,
    deckSignature: canonicalDeck.join('|'),
    used: Boolean(record.used),
  };
}

export function buildPracticeRecommendationCandidates(records = [], { catalog = null } = {}) {
  const bestByBossAndDeck = new Map();

  (Array.isArray(records) ? records : []).forEach((record, index) => {
    const candidate = normalizePracticeRecommendationCandidate(record, index, { catalog });
    if (!candidate || !candidate.bossNo) return;
    const key = candidateKey(candidate);
    const existing = bestByBossAndDeck.get(key);
    if (!existing || candidate.damageEok > existing.damageEok
      || (candidate.damageEok === existing.damageEok && candidate.slotNo < existing.slotNo)) {
      bestByBossAndDeck.set(key, candidate);
    }
  });

  return [...bestByBossAndDeck.values()].sort((a, b) => b.damageEok - a.damageEok
    || a.bossNo - b.bossNo
    || a.slotNo - b.slotNo
    || a.id.localeCompare(b.id));
}

export function decksDoNotOverlap(candidates = []) {
  const seen = new Set();
  for (const candidate of Array.isArray(candidates) ? candidates : []) {
    for (const code of candidate?.canonicalDeck || []) {
      if (!code || seen.has(code)) return false;
      seen.add(code);
    }
  }
  return true;
}

function combinationSignature(attacks = []) {
  return attacks
    .map((attack) => attack.id)
    .sort()
    .join('|');
}

export function findTopPracticeCombinations(records = [], {
  limit = 5,
  bossNo = null,
  catalog = null,
} = {}) {
  const candidates = buildPracticeRecommendationCandidates(records, { catalog });
  const normalizedBossNo = numberValue(bossNo, 0);
  const combinations = [];
  const seen = new Set();

  for (let first = 0; first < candidates.length - 2; first += 1) {
    for (let second = first + 1; second < candidates.length - 1; second += 1) {
      const firstPair = [candidates[first], candidates[second]];
      if (!decksDoNotOverlap(firstPair)) continue;

      for (let third = second + 1; third < candidates.length; third += 1) {
        const attacks = [...firstPair, candidates[third]];
        if (normalizedBossNo && !attacks.some((attack) => attack.bossNo === normalizedBossNo)) continue;
        if (!decksDoNotOverlap(attacks)) continue;

        const signature = combinationSignature(attacks);
        if (seen.has(signature)) continue;
        seen.add(signature);

        const orderedAttacks = [...attacks].sort((a, b) => a.slotNo - b.slotNo
          || a.bossNo - b.bossNo
          || b.damageEok - a.damageEok);
        combinations.push({
          key: signature,
          attacks: orderedAttacks,
          totalDamageEok: attacks.reduce((sum, attack) => sum + attack.damageEok, 0),
        });
      }
    }
  }

  return combinations
    .sort((a, b) => b.totalDamageEok - a.totalDamageEok
      || b.attacks[0].damageEok - a.attacks[0].damageEok
      || a.key.localeCompare(b.key))
    .slice(0, Math.max(1, numberValue(limit, 5)));
}

export function buildMemberPracticeRecommendations({
  members = [],
  practiceRecords = [],
  limit = 5,
  bossNo = null,
  catalog = null,
} = {}) {
  const records = Array.isArray(practiceRecords) ? practiceRecords : [];
  const memberList = Array.isArray(members) ? members : [];

  return memberList.map((member) => {
    const openid = textValue(member.openid ?? member.memberOpenid ?? member.member_openid);
    const nickname = textValue(member.nickname);
    const normalizedOpenid = normalizedMemberKey(openid);
    const normalizedNickname = normalizedMemberKey(nickname);
    const memberRecords = records.filter((record) => {
      const recordOpenid = normalizedMemberKey(memberKey(record));
      if (normalizedOpenid && recordOpenid) return normalizedOpenid === recordOpenid;
      return Boolean(normalizedNickname && normalizedNickname === normalizedMemberKey(memberNickname(record)));
    });

    const candidates = buildPracticeRecommendationCandidates(memberRecords, { catalog });
    return {
      member,
      candidateCount: candidates.length,
      combinations: findTopPracticeCombinations(memberRecords, {
        limit,
        bossNo,
        catalog,
      }),
    };
  });
}
